/**
 * AGC: Potion Crafting Component - v6.8.0
 * AGC: Allows players to craft potions using on-chain material NFTs and OGB
 * AGC: All-in-one crafting: burn materials + pay OGB + mint potion NFT
 * AGC: Uses new PotionCraftingEngine system with 18 potions across 4 tiers
 * AGC: Redesigned following Profile.tsx pattern - compact game-style UI
 * AGC: NOW INTEGRATED WITH ON-CHAIN CONTRACTS (CraftMaterial1155 + CraftedPotion1155)
 * @author AGC (AI-Generated Code) with Cursor
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { formatEther } from 'viem'
import { FlaskConical, TrendingUp, Shield, Sword, Zap, Snowflake, Coins, X, Heart, Droplets, ChevronLeft, ChevronRight } from 'lucide-react'
import { useContract } from '../hooks/useContract'
import { buildBackendUrl } from '../utils/backendUrl'
import {
  PotionTier,
  PotionEffectType,
  POTION_DEFINITIONS,
  CraftItemType,
} from '../game/PotionCraftingEngine'

// AGC: Potion tier definitions (matching PotionCraftingEngine)
const POTION_TIERS = [
  { 
    id: PotionTier.Minor, 
    name: 'Minor', 
    emoji: '⚪', 
    color: 'from-gray-600 to-gray-500', 
    borderColor: 'border-gray-500',
    description: 'Easy - 50% effect, 3 items, 2 types'
  },
  { 
    id: PotionTier.Large, 
    name: 'Large', 
    emoji: '🟢', 
    color: 'from-green-600 to-emerald-500', 
    borderColor: 'border-green-500',
    description: 'Normal - 75% effect, 5 items, 3 types'
  },
  { 
    id: PotionTier.Greater, 
    name: 'Greater', 
    emoji: '🔵', 
    color: 'from-blue-600 to-cyan-500', 
    borderColor: 'border-blue-500',
    description: 'Hard - 100% effect, 10 items, 4 types'
  },
  { 
    id: PotionTier.Major, 
    name: 'Major', 
    emoji: '🟡', 
    color: 'from-yellow-600 to-orange-500', 
    borderColor: 'border-yellow-500',
    description: 'Insane - 150% effect, 20 items, 4 types'
  },
] as const

// AGC: Potion effect types (matching PotionCraftingEngine)
const EFFECT_TYPES = [
  { id: PotionEffectType.Shield, name: 'Shield', icon: Shield, emoji: '🛡️', description: '+25% defense (base)' },
  { id: PotionEffectType.Pierce, name: 'Pierce', icon: TrendingUp, emoji: '📈', description: '+25% attack (base)' },
  { id: PotionEffectType.Haste, name: 'Haste', icon: Zap, emoji: '⚡', description: '+5 flat damage (base)' },
  { id: PotionEffectType.Frost, name: 'Frost', icon: Snowflake, emoji: '❄️', description: 'Freeze 1 turn (Greater only)' },
  { id: PotionEffectType.Heal, name: 'Heal', icon: Heart, emoji: '❤️', description: '+10 HP (base)' },
  { id: PotionEffectType.ManaRegen, name: 'Mana Regen', icon: Droplets, emoji: '💧', description: '+5% mana regen (base)' },
  { id: PotionEffectType.DoubleStrike, name: 'Double Strike', icon: Sword, emoji: '⚔️', description: '+30% damage on win (base)' },
  { id: PotionEffectType.Poison, name: 'Poison', icon: Coins, emoji: '☠️', description: '2.5 dmg × 5 turns (base)' },
] as const

interface Materials {
  herb: number
  crystal: number
  essence: number
  shard: number
}

interface CraftingPreview {
  recipe: {
    ogbCost: string
    baseSuccess: number
    materialCost: Materials
  }
  farmPro: number
  successRate: number
  magnitude: number
  hasMaterials: boolean
}

interface PotionCraftingProps {
  address: string | undefined
  isConnected: boolean
  ogbBalance: number
}

// AGC: Contract addresses for checking if on-chain crafting is configured
const CRAFT_MATERIAL_ADDRESS = import.meta.env.VITE_CRAFT_MATERIAL1155_ADDRESS || '0x0000000000000000000000000000000000000000'
const CRAFTED_POTION_ADDRESS = import.meta.env.VITE_CRAFTED_POTION1155_ADDRESS || '0x0000000000000000000000000000000000000000'

export const PotionCrafting: React.FC<PotionCraftingProps> = ({ address, isConnected, ogbBalance }) => {
  // AGC: Get useContract hook (with type assertion for new functions)
  const contractHook = useContract() as ReturnType<typeof useContract> & {
    getMaterialBalances?: (playerAddress: string) => Promise<Materials>
    getCraftingPrices?: (userAddress: string) => Promise<{ ogbPriceWei: bigint; ogbPrice: string; hasDiscount: boolean; usdPriceMicro: number; usdPrice: number }>
    craftPotion?: (potionTypeId: number, farmPro: number) => Promise<{ receipt: any; potionData: { tokenId: number; potionTypeId: number; rarity: number; magnitude: number; ogbPaid: bigint } | null }>
  }
  
  const { 
    approveOGBForCrafting, 
    getMaterialBalances, 
    getCraftingPrices,
    craftPotion: craftPotionOnChain
  } = contractHook
  
  // AGC: Use refs to store contract functions to prevent infinite re-render loops
  // AGC: These functions are recreated on every render, so we store them in refs
  // AGC: FIXED - Initialize refs as null and update synchronously
  const getMaterialBalancesRef = useRef<typeof getMaterialBalances | null>(null)
  const getCraftingPricesRef = useRef<typeof getCraftingPrices | null>(null)
  const craftPotionOnChainRef = useRef<typeof craftPotionOnChain | null>(null)
  
  // AGC: Update refs synchronously (not in useEffect) to ensure they're always current
  getMaterialBalancesRef.current = getMaterialBalances
  getCraftingPricesRef.current = getCraftingPrices
  craftPotionOnChainRef.current = craftPotionOnChain
  
  const [showComingSoon, setShowComingSoon] = useState(false) // AGC: Changed to false - feature is ready
  const [selectedTier, setSelectedTier] = useState<PotionTier>(PotionTier.Minor)
  const [selectedEffect, setSelectedEffect] = useState<PotionEffectType>(PotionEffectType.Haste)
  const [materials, setMaterials] = useState<Materials>({ herb: 0, crystal: 0, essence: 0, shard: 0 })
  const [preview, setPreview] = useState<CraftingPreview | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isCrafting, setIsCrafting] = useState(false)
  const [craftResult, setCraftResult] = useState<{ success: boolean; message: string } | null>(null)
  const [onChainCraftingPrice, setOnChainCraftingPrice] = useState<{ ogbPrice: string; hasDiscount: boolean; usdPrice: number } | null>(null)
  // AGC: FIXED (v6.8.2) - Initialize isContractConfigured synchronously to prevent race condition
  // AGC: Previously was set in useEffect which caused backend fallback to be used on first render
  const isContractConfigured = useMemo(() => {
    const configured = 
      CRAFT_MATERIAL_ADDRESS !== '0x0000000000000000000000000000000000000000' &&
      CRAFTED_POTION_ADDRESS !== '0x0000000000000000000000000000000000000000'
    console.log('🔧 On-chain crafting configured:', configured, { CRAFT_MATERIAL_ADDRESS, CRAFTED_POTION_ADDRESS })
    return configured
  }, [])
  
  // AGC: Rarity result popup state
  const [rarityResult, setRarityResult] = useState<{ rarity: number; rarityName: string; potionName: string; tokenId: number; magnitude: number } | null>(null)
  
  // AGC: Get available effect types for selected tier
  const availableEffectsForTier = useMemo(() => {
    const effectsInTier = new Set<PotionEffectType>()
    POTION_DEFINITIONS.forEach(p => {
      if (p.tier === selectedTier) {
        effectsInTier.add(p.effect.type)
      }
    })
    return Array.from(effectsInTier)
  }, [selectedTier])
  
  // AGC: Ensure selected effect is available for current tier, if not switch to first available
  useEffect(() => {
    if (availableEffectsForTier.length > 0 && !availableEffectsForTier.includes(selectedEffect)) {
      setSelectedEffect(availableEffectsForTier[0])
    }
  }, [selectedTier, availableEffectsForTier, selectedEffect])
  
  // AGC: Get available potions for selected tier and effect
  const availablePotions = useMemo(() => {
    return POTION_DEFINITIONS.filter(
      p => p.tier === selectedTier && p.effect.type === selectedEffect
    )
  }, [selectedTier, selectedEffect])
  
  // AGC: Get the first available potion (or null if none)
  const selectedPotion = availablePotions[0] || null

  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)' }

  // AGC: Load materials inventory (ALWAYS from on-chain when contracts configured, never Firebase)
  // AGC: FIXED - Always fetch from on-chain, refresh after crafting
  // AGC: FIXED (v6.8.1) - Use getMaterialBalances directly from hook to ensure stability
  // AGC: FIXED (v6.8.2) - Added detailed logging to debug inconsistent values
  const loadMaterialsFromChain = useCallback(async () => {
    console.log('🔄 loadMaterialsFromChain called', { 
      isConnected, 
      address, 
      isContractConfigured,
      hasMaterialsFn: !!getMaterialBalancesRef.current 
    })
    
    if (!isConnected || !address) {
      console.log('⚠️ Not connected or no address - setting materials to 0')
      setMaterials({ herb: 0, crystal: 0, essence: 0, shard: 0 })
      return
    }

    try {
      // AGC: ALWAYS use on-chain if contracts are configured (never Firebase)
      // AGC: Use the ref's current value which is always up-to-date
      const getMaterialsFn = getMaterialBalancesRef.current
      if (isContractConfigured && getMaterialsFn) {
        console.log('📦 Fetching materials from on-chain for address:', address)
        const onChainMaterials = await getMaterialsFn(address)
        console.log('📦 Loaded materials from on-chain:', JSON.stringify(onChainMaterials))
        setMaterials(onChainMaterials)
        return
      }
      
      // AGC: Only fallback to backend if contracts NOT configured (legacy mode)
      console.log('⚠️ Contracts not configured, using backend fallback (legacy)')
      const backendUrl = buildBackendUrl()
      const response = await fetch(`${backendUrl}/api/crafting/materials/${address}`)
      if (response.ok) {
        const data = await response.json()
        console.log('📦 Loaded materials from backend:', JSON.stringify(data.materials))
        setMaterials(data.materials || { herb: 0, crystal: 0, essence: 0, shard: 0 })
      }
    } catch (error) {
      console.error('❌ Error loading materials:', error)
    }
  }, [address, isConnected, isContractConfigured])

  // AGC: Load materials on mount and when dependencies change
  useEffect(() => {
    loadMaterialsFromChain()
  }, [loadMaterialsFromChain])
  
  // AGC: Track if prices have been loaded to prevent duplicate loads
  const [pricesLoaded, setPricesLoaded] = useState(false)
  
  // AGC: Load crafting prices (on-chain)
  // AGC: FIXED - Using refs to prevent infinite re-render loop
  useEffect(() => {
    if (!isConnected || !address || !isContractConfigured) {
      setOnChainCraftingPrice(null)
      setPricesLoaded(false)
      return
    }

    // AGC: Prevent loading if already loaded for this address
    if (pricesLoaded) return

    const loadPrices = async () => {
      try {
        const getCraftingPricesFn = getCraftingPricesRef.current
        if (getCraftingPricesFn) {
          const prices = await getCraftingPricesFn(address)
          setOnChainCraftingPrice({
            ogbPrice: prices.ogbPrice,
            hasDiscount: prices.hasDiscount,
            usdPrice: prices.usdPrice
          })
          setPricesLoaded(true)
          console.log('💰 Loaded on-chain crafting prices:', prices)
        }
      } catch (error) {
        console.error('❌ Error loading crafting prices:', error)
      }
    }

    loadPrices()
  }, [address, isConnected, isContractConfigured, pricesLoaded])

  // AGC: Calculate crafting preview from selected potion
  useEffect(() => {
    if (!isConnected || !address || !selectedPotion) {
      setPreview(null)
      return
    }

    const loadPreview = async () => {
      try {
        setIsLoadingPreview(true)
        const backendUrl = buildBackendUrl()
        
        // AGC: Get FarmPro for success rate calculation
        const farmProResponse = await fetch(`${backendUrl}/api/farmpro/${address}`)
        const farmProData = farmProResponse.ok ? await farmProResponse.json() : { farmPro: 0 }
        
        // AGC: Calculate recipe total items for OGB cost estimation
        const totalItems = Object.values(selectedPotion.recipe).reduce((sum, val) => sum + (val || 0), 0)
        const estimatedOGBCost = BigInt(totalItems * 1e15) // AGC: Rough estimate: 0.001 OGB per item
        
        // AGC: Base success rate (will be improved with FarmPro)
        const baseSuccess = 0.5 // AGC: 50% base success
        const farmPro = farmProData.farmPro || 0
        const successRate = Math.min(0.95, baseSuccess + (farmPro * 0.3)) // AGC: FarmPro boosts up to 95%
        
        // AGC: Calculate magnitude from potion effect
        let magnitude = 0
        switch (selectedPotion.effect.type) {
          case PotionEffectType.Shield:
            magnitude = selectedPotion.effect.defenseBonus / 100
            break
          case PotionEffectType.Pierce:
            magnitude = selectedPotion.effect.attackBonus / 100
            break
          case PotionEffectType.Haste:
            magnitude = selectedPotion.effect.flatDamage / 10 // AGC: Normalize to 0-1 range
            break
          case PotionEffectType.Heal:
            magnitude = selectedPotion.effect.hpRestored / 100 // AGC: Normalize to 0-1 range
            break
          case PotionEffectType.ManaRegen:
            magnitude = selectedPotion.effect.regenPercent / 100
            break
          case PotionEffectType.DoubleStrike:
            magnitude = selectedPotion.effect.damageBonus / 100
            break
          case PotionEffectType.Poison:
            magnitude = selectedPotion.effect.damagePerTurn / 10 // AGC: Normalize to 0-1 range
            break
          case PotionEffectType.Frost:
            magnitude = selectedPotion.effect.freezeTurns / 2 // AGC: Normalize to 0-1 range
            break
        }
        
        setPreview({
          recipe: {
            ogbCost: estimatedOGBCost.toString(),
            baseSuccess,
            materialCost: {
              herb: selectedPotion.recipe[CraftItemType.Herb] || 0,
              crystal: selectedPotion.recipe[CraftItemType.Crystal] || 0,
              essence: selectedPotion.recipe[CraftItemType.Essence] || 0,
              shard: selectedPotion.recipe[CraftItemType.Shard] || 0,
            }
          },
          farmPro,
          successRate,
          magnitude,
          hasMaterials: true // AGC: Will be checked separately
        })
      } catch (error) {
        console.error('❌ Error loading crafting preview:', error)
        setPreview(null)
      } finally {
        setIsLoadingPreview(false)
      }
    }

    loadPreview()
  }, [address, isConnected, selectedPotion])

  // AGC: Check if user has required materials
  const hasRequiredMaterials = useMemo(() => {
    if (!preview) return false
    
    const required = preview.recipe.materialCost
    return (
      materials.herb >= required.herb &&
      materials.crystal >= required.crystal &&
      materials.essence >= required.essence &&
      materials.shard >= required.shard
    )
  }, [materials, preview])

  // AGC: Check if user has enough OGB
  const hasEnoughOGB = useMemo(() => {
    // AGC: Use on-chain price if available, otherwise fallback to preview
    if (onChainCraftingPrice) {
      const ogbCost = parseFloat(onChainCraftingPrice.ogbPrice)
      return ogbBalance >= ogbCost
    }
    if (!preview) return false
    const ogbCost = parseFloat(formatEther(BigInt(preview.recipe.ogbCost)))
    return ogbBalance >= ogbCost
  }, [preview, ogbBalance, onChainCraftingPrice])

  // AGC: Get potion type ID for on-chain contract (0-17)
  const getPotionTypeId = useCallback((): number => {
    if (!selectedPotion) return 0
    
    // AGC: Map potion definitions to on-chain type IDs
    const index = POTION_DEFINITIONS.findIndex(p => p.id === selectedPotion.id)
    return index >= 0 ? index : 0
  }, [selectedPotion])

  // AGC: Handle crafting (on-chain or backend fallback)
  const handleCraft = async () => {
    if (!address || !hasRequiredMaterials || !hasEnoughOGB || !preview) {
      return
    }

    try {
      setIsCrafting(true)
      setCraftResult(null)

      // AGC: NEW - Use on-chain crafting if contract is configured
      const craftPotionFn = craftPotionOnChainRef.current
      if (isContractConfigured && craftPotionFn) {
        console.log('🔮 Crafting on-chain...')
        
        // AGC: Get FarmPro for rarity roll
        const backendUrl = buildBackendUrl()
        const farmProResponse = await fetch(`${backendUrl}/api/farmpro/${address}`)
        const farmProData = farmProResponse.ok ? await farmProResponse.json() : { farmPro: 0 }
        const farmPro = farmProData.farmPro || 0
        
        // AGC: Get potion type ID (0-17)
        const potionTypeId = getPotionTypeId()
        console.log(`🎯 Crafting potion type ${potionTypeId} (FarmPro: ${Math.round(farmPro * 100)})`)
        
        try {
          const result = await craftPotionFn(potionTypeId, farmPro)
          
          if (result?.potionData) {
            const rarityNames = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary']
            const rarityName = rarityNames[result.potionData.rarity] || 'Unknown'
            const potionName = selectedPotion?.displayName || 'Potion'
            
            // AGC: Show rarity result popup
            setRarityResult({
              rarity: result.potionData.rarity,
              rarityName,
              potionName,
              tokenId: result.potionData.tokenId,
              magnitude: result.potionData.magnitude
            })
            
            // AGC: Auto-close popup after 5 seconds
            setTimeout(() => {
              setRarityResult(null)
            }, 5000)
            
            // AGC: Refresh materials after successful craft (force reload from on-chain)
            // AGC: Wait for transaction to be confirmed, then refresh multiple times to catch updates
            if (isContractConfigured) {
              // AGC: Refresh immediately (might be cached, but try anyway)
              setTimeout(() => {
                loadMaterialsFromChain()
                console.log('📦 Refreshing materials after crafting (immediate)')
              }, 1000)
              
              // AGC: Refresh again after 3 seconds (block confirmation)
              setTimeout(() => {
                loadMaterialsFromChain()
                console.log('📦 Refreshed materials after crafting (3s)')
              }, 3000)
              
              // AGC: Final refresh after 6 seconds (ensure blockchain fully updated)
              setTimeout(() => {
                loadMaterialsFromChain()
                console.log('📦 Final materials refresh after crafting (6s)')
              }, 6000)
            }
          } else {
            setCraftResult({ success: true, message: '✅ Potion crafted!' })
          }
          
          return
        } catch (craftError: any) {
          console.error('❌ On-chain crafting failed:', craftError)
          throw new Error(craftError.message || 'Crafting failed')
        }
      }
      
      // AGC: FALLBACK - Use backend crafting (legacy)
      console.log('🔮 Using backend crafting (legacy)...')

      // AGC: Step 1: Approve OGB spending for backend wallet
      const ogbCostWei = BigInt(preview.recipe.ogbCost)
      console.log(`🔐 Approving ${formatEther(ogbCostWei)} OGB for crafting...`)
      
      try {
        await approveOGBForCrafting(ogbCostWei)
        console.log('✅ OGB approval confirmed')
      } catch (approvalError: any) {
        throw new Error(`OGB approval failed: ${approvalError.message || 'Unknown error'}`)
      }

      // AGC: Step 2: Call backend to craft potion (backend will transfer OGB)
      const backendUrl = buildBackendUrl()
      const tierMap: Record<PotionTier, string> = {
        [PotionTier.Minor]: 'COMMON',
        [PotionTier.Large]: 'UNCOMMON',
        [PotionTier.Greater]: 'RARE',
        [PotionTier.Major]: 'EPIC',
      }
      const effectMap: Record<PotionEffectType, string> = {
        [PotionEffectType.Shield]: 'SHIELD',
        [PotionEffectType.Pierce]: 'PIERCE',
        [PotionEffectType.Haste]: 'HASTE',
        [PotionEffectType.Frost]: 'FROST',
        [PotionEffectType.Heal]: 'DAMAGE',
        [PotionEffectType.ManaRegen]: 'REWARD_MULT',
        [PotionEffectType.DoubleStrike]: 'DAMAGE',
        [PotionEffectType.Poison]: 'DAMAGE',
      }
      
      const response = await fetch(`${backendUrl}/api/crafting/craft/${address}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: tierMap[selectedTier] || 'COMMON',
          effectType: effectMap[selectedEffect] || 'DAMAGE'
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Crafting failed')
      }

      const result = await response.json()
      
      if (result.result?.success) {
        const potionName = selectedPotion?.displayName || `${selectedTier} ${selectedEffect}`
        setCraftResult({ success: true, message: `✅ Successfully crafted ${potionName}!` })
        // AGC: Refresh materials after successful craft
        const materialsResponse = await fetch(`${backendUrl}/api/crafting/materials/${address}`)
        if (materialsResponse.ok) {
          const materialsData = await materialsResponse.json()
          setMaterials(materialsData.materials || materials)
        }
      } else {
        setCraftResult({ success: false, message: `❌ Crafting failed! You lost the materials and OGB cost.` })
      }
    } catch (error: any) {
      console.error('❌ Crafting error:', error)
      setCraftResult({ success: false, message: error.message || 'Crafting failed' })
    } finally {
      setIsCrafting(false)
    }
  }

  const selectedTierData = POTION_TIERS.find(t => t.id === selectedTier)
  const selectedEffectData = EFFECT_TYPES.find(e => e.id === selectedEffect)
  
  // AGC: Show available potions count for selected tier+effect
  const potionCount = availablePotions.length

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5 min-h-screen"
      style={sharedTopPaddingStyle}
    >
      {/* AGC: Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" style={sharedTopPaddingStyle}>
          <div className="bg-gradient-to-br from-slate-950 to-slate-900 border border-white/10 rounded-2xl p-6 mx-4 max-w-sm shadow-[0_12px_32px_rgba(0,0,0,0.6)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-purple-400" />
                Crafted Potions
              </h2>
              <button
                onClick={() => setShowComingSoon(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🔮</div>
              <h3 className="text-lg font-semibold text-white mb-2">Coming Soon</h3>
              <p className="text-sm text-slate-400">
                Craft powerful potions using materials and OGB tokens. This feature is currently under development.
              </p>
            </div>
            <button
              onClick={() => setShowComingSoon(false)}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-xl text-sm font-semibold shadow-lg shadow-purple-500/30 transition-all"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* AGC: Hero Section - Following Profile.tsx pattern */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">CRAFTING</p>
            <h1 className="text-2xl font-bold leading-tight text-white flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-purple-400" />
              Potion Crafting
            </h1>
            <p className="text-[11px] text-slate-400 mt-1">Create powerful potions with materials</p>
          </div>
        </div>
      </section>

      {/* AGC: Materials Grid - Compact 4-column layout like Profile stats */}
      <section className="grid grid-cols-4 gap-1.5 text-[11px]">
        {[
          { icon: '🌿', label: 'Herb', value: materials.herb, accent: 'text-emerald-300' },
          { icon: '💎', label: 'Crystal', value: materials.crystal, accent: 'text-cyan-300' },
          { icon: '✨', label: 'Essence', value: materials.essence, accent: 'text-purple-300' },
          { icon: '🔮', label: 'Shard', value: materials.shard, accent: 'text-pink-300' },
        ].map((material) => (
          <div
            key={material.label}
            className="rounded-xl bg-gradient-to-br from-slate-950 to-slate-900 border border-white/5 px-2 py-2 shadow-[0_8px_20px_rgba(0,0,0,0.45)] hover:bg-slate-900/90 transition-colors"
          >
            <div className="flex items-center gap-1 text-[10px] text-slate-300 uppercase tracking-wide">
              <span>{material.icon}</span>
              {material.label}
            </div>
            <div className={`text-2xl font-extrabold mt-0.5 ${material.accent}`}>{material.value}</div>
            <div className="text-[9px] text-slate-500">
              {isContractConfigured ? 'On-chain' : 'Available'}
            </div>
          </div>
        ))}
        {/* AGC: Refresh button for materials (only show when on-chain) */}
        {isContractConfigured && (
          <button
            onClick={loadMaterialsFromChain}
            className="col-span-4 mt-1 px-3 py-1.5 bg-slate-800/60 hover:bg-slate-700/60 rounded-lg border border-white/10 text-[10px] text-slate-300 hover:text-white transition-colors flex items-center justify-center gap-1"
            title="Refresh materials from blockchain"
          >
            <span>🔄</span>
            Refresh Materials
          </button>
        )}
      </section>
      {/* AGC: Tier Selector - Single display with arrow navigation */}
      <section className="pb-2 border-b border-white/10">
        <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2">POTION TIER</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const currentIndex = POTION_TIERS.findIndex(t => t.id === selectedTier)
              const prevIndex = currentIndex > 0 ? currentIndex - 1 : POTION_TIERS.length - 1
              setSelectedTier(POTION_TIERS[prevIndex].id)
            }}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl border border-white/10 bg-gradient-to-b from-slate-800/80 to-slate-900/80 shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)] hover:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_4px_8px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 active:scale-[0.96] active:translate-y-0 transition-all duration-150"
            aria-label="Previous tier"
          >
            <ChevronLeft className="w-5 h-5 text-slate-300 drop-shadow-sm" />
          </button>
          
          <div className="flex-1 flex items-center justify-center h-10">
            {(() => {
              const currentTier = POTION_TIERS.find(t => t.id === selectedTier)
              if (!currentTier) return null
              
              // AGC: Map tier colors to modern 3D styling
              const tierStyles: Record<string, { gradient: string; border: string; glow: string; highlight: string }> = {
                'Minor': {
                  gradient: 'from-slate-700/90 via-slate-800/90 to-slate-900/90',
                  border: 'border-slate-600/40',
                  glow: 'shadow-[0_0_0_1px_rgba(255,255,255,0.1),inset_0_1px_0_rgba(255,255,255,0.15),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                  highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-white/20 before:rounded-t-[10px]'
                },
                'Large': {
                  gradient: 'from-emerald-600/90 via-green-700/90 to-emerald-800/90',
                  border: 'border-emerald-500/50',
                  glow: 'shadow-[0_0_12px_rgba(16,185,129,0.2),0_0_0_1px_rgba(16,185,129,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                  highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-emerald-300/40 before:rounded-t-[10px]'
                },
                'Greater': {
                  gradient: 'from-blue-600/90 via-cyan-700/90 to-blue-800/90',
                  border: 'border-blue-500/50',
                  glow: 'shadow-[0_0_12px_rgba(59,130,246,0.2),0_0_0_1px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                  highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-cyan-300/40 before:rounded-t-[10px]'
                },
                'Major': {
                  gradient: 'from-amber-600/90 via-yellow-700/90 to-orange-800/90',
                  border: 'border-amber-500/50',
                  glow: 'shadow-[0_0_12px_rgba(245,158,11,0.2),0_0_0_1px_rgba(245,158,11,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                  highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-amber-300/40 before:rounded-t-[10px]'
                }
              }
              
              const style = tierStyles[currentTier.name] || tierStyles['Minor']
              
              return (
                <div className={`relative flex-1 h-10 flex items-center justify-center px-4 rounded-[10px] border bg-gradient-to-b ${style.gradient} text-white ${style.border} ${style.glow} ${style.highlight}`}>
                  <div className="text-sm font-bold uppercase tracking-wide drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">{currentTier.name}</div>
                </div>
              )
            })()}
          </div>
          
          <button
            onClick={() => {
              const currentIndex = POTION_TIERS.findIndex(t => t.id === selectedTier)
              const nextIndex = currentIndex < POTION_TIERS.length - 1 ? currentIndex + 1 : 0
              setSelectedTier(POTION_TIERS[nextIndex].id)
            }}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl border border-white/10 bg-gradient-to-b from-slate-800/80 to-slate-900/80 shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)] hover:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_4px_8px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 active:scale-[0.96] active:translate-y-0 transition-all duration-150"
            aria-label="Next tier"
          >
            <ChevronRight className="w-5 h-5 text-slate-300 drop-shadow-sm" />
          </button>
        </div>
      </section>

      {/* AGC: Effect Type Selector - Single display with arrow navigation (only shows effects available for selected tier) */}
      <section className="pb-2 border-b border-white/10">
        <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-2">EFFECT TYPE</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const currentIndex = availableEffectsForTier.findIndex(e => e === selectedEffect)
              const prevIndex = currentIndex > 0 ? currentIndex - 1 : availableEffectsForTier.length - 1
              if (availableEffectsForTier[prevIndex]) {
                setSelectedEffect(availableEffectsForTier[prevIndex])
              }
            }}
            disabled={availableEffectsForTier.length === 0}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl border border-white/10 bg-gradient-to-b from-slate-800/80 to-slate-900/80 shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)] hover:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_4px_8px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 active:scale-[0.96] active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)] transition-all duration-150"
            aria-label="Previous effect"
          >
            <ChevronLeft className="w-5 h-5 text-slate-300 drop-shadow-sm" />
          </button>
          
          <div className="flex-1 flex items-center justify-center h-10">
            {(() => {
              const currentEffect = EFFECT_TYPES.find(e => e.id === selectedEffect)
              if (!currentEffect || availableEffectsForTier.length === 0) {
                return (
                  <div className="flex-1 h-10 flex items-center justify-center px-4 rounded-[10px] border border-slate-700/50 bg-gradient-to-b from-slate-800/60 via-slate-900/60 to-slate-950/60 text-slate-400 shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)]">
                    <div className="text-sm font-bold uppercase">No Effects</div>
                  </div>
                )
              }
              
              // AGC: Modern 3D color mapping with proper gradients and effects
              const getEffectStyle = (effectType: PotionEffectType) => {
                switch (effectType) {
                  case PotionEffectType.Heal:
                  case PotionEffectType.Shield:
                    return {
                      gradient: 'from-red-600/90 via-rose-700/90 to-red-800/90',
                      border: 'border-red-500/50',
                      glow: 'shadow-[0_0_12px_rgba(239,68,68,0.2),0_0_0_1px_rgba(239,68,68,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                      highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-red-300/40 before:rounded-t-[10px]'
                    }
                  case PotionEffectType.Pierce:
                  case PotionEffectType.DoubleStrike:
                    return {
                      gradient: 'from-purple-600/90 via-violet-700/90 to-purple-800/90',
                      border: 'border-purple-500/50',
                      glow: 'shadow-[0_0_12px_rgba(168,85,247,0.2),0_0_0_1px_rgba(168,85,247,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                      highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-purple-300/40 before:rounded-t-[10px]'
                    }
                  case PotionEffectType.Poison:
                  case PotionEffectType.Haste:
                    return {
                      gradient: 'from-emerald-600/90 via-green-700/90 to-emerald-800/90',
                      border: 'border-emerald-500/50',
                      glow: 'shadow-[0_0_12px_rgba(16,185,129,0.2),0_0_0_1px_rgba(16,185,129,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                      highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-emerald-300/40 before:rounded-t-[10px]'
                    }
                  case PotionEffectType.ManaRegen:
                  case PotionEffectType.Frost:
                    return {
                      gradient: 'from-blue-600/90 via-cyan-700/90 to-blue-800/90',
                      border: 'border-blue-500/50',
                      glow: 'shadow-[0_0_12px_rgba(59,130,246,0.2),0_0_0_1px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                      highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-cyan-300/40 before:rounded-t-[10px]'
                    }
                  default:
                    return {
                      gradient: 'from-cyan-600/90 via-blue-700/90 to-cyan-800/90',
                      border: 'border-cyan-500/50',
                      glow: 'shadow-[0_0_12px_rgba(6,182,212,0.2),0_0_0_1px_rgba(6,182,212,0.3),inset_0_1px_0_rgba(255,255,255,0.2),inset_0_-2px_8px_rgba(0,0,0,0.4)]',
                      highlight: 'before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-cyan-300/40 before:rounded-t-[10px]'
                    }
                }
              }
              
              const style = getEffectStyle(currentEffect.id)
              
              return (
                <div className={`relative flex-1 h-10 flex items-center justify-center px-4 rounded-[10px] border bg-gradient-to-b ${style.gradient} text-white ${style.border} ${style.glow} ${style.highlight}`}>
                  <div className="text-sm font-bold uppercase tracking-wide drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">{currentEffect.name}</div>
                </div>
              )
            })()}
          </div>
          
          <button
            onClick={() => {
              const currentIndex = availableEffectsForTier.findIndex(e => e === selectedEffect)
              const nextIndex = currentIndex < availableEffectsForTier.length - 1 ? currentIndex + 1 : 0
              if (availableEffectsForTier[nextIndex]) {
                setSelectedEffect(availableEffectsForTier[nextIndex])
              }
            }}
            disabled={availableEffectsForTier.length === 0}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl border border-white/10 bg-gradient-to-b from-slate-800/80 to-slate-900/80 shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)] hover:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_4px_8px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 active:scale-[0.96] active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)] transition-all duration-150"
            aria-label="Next effect"
          >
            <ChevronRight className="w-5 h-5 text-slate-300 drop-shadow-sm" />
          </button>
        </div>
      </section>

      {/* AGC: Available Potions Info */}
      {selectedPotion && (
        <section className="bg-slate-950/40 border border-slate-800 rounded-xl p-2">
          <p className="text-[10px] text-slate-400 text-center">
            {potionCount > 0 ? (
              <>📋 <span className="text-white font-semibold">{selectedPotion.displayName}</span> available</>
            ) : (
              <>⚠️ No {selectedTierData?.name} {selectedEffectData?.name} potion available</>
            )}
          </p>
        </section>
      )}

      {/* AGC: Crafting Preview - Compact game-style panel */}
      {preview && selectedPotion && (
        <section className="bg-slate-950/60 border border-slate-900 rounded-2xl p-3 shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
          <div className="flex items-center gap-3 mb-3 pb-2 border-b border-white/10">
            {/* AGC: Left - Tier potion image */}
            {(() => {
              // AGC: Map tier name to file prefix
              const tierPrefix = selectedTierData?.name.toLowerCase() === 'minor' ? 'minor' :
                                selectedTierData?.name.toLowerCase() === 'large' ? 'larger' :
                                selectedTierData?.name.toLowerCase() === 'greater' ? 'greater' :
                                'major'
              
              // AGC: Map effect type to color for tier image
              const getEffectColor = (effectType: PotionEffectType): string => {
                switch (effectType) {
                  case PotionEffectType.Heal:
                  case PotionEffectType.Shield:
                    return 'red'
                  case PotionEffectType.Pierce:
                  case PotionEffectType.DoubleStrike:
                    return 'purple'
                  case PotionEffectType.Poison:
                  case PotionEffectType.Haste:
                    return 'green'
                  case PotionEffectType.ManaRegen:
                  case PotionEffectType.Frost:
                    return 'blue'
                  default:
                    return 'green'
                }
              }
              
              const color = getEffectColor(selectedPotion.effect.type)
              // AGC: Try multiple naming patterns (some files use _color, some use _.color)
              const tierImagePaths = [
                `/${tierPrefix}_${color}.png`,
                `/${tierPrefix}_.${color}.png`,
                `/${tierPrefix}${color}.png` // AGC: Fallback for minor_green pattern
              ]
              
              return (
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                  <img 
                    src={tierImagePaths[0]} 
                    alt={`${selectedTierData?.name} ${color} potion`}
                    className="w-full h-full object-contain drop-shadow-lg"
                    onError={(e) => {
                      // AGC: Try alternative naming patterns
                      const currentSrc = e.currentTarget.src
                      const currentIndex = tierImagePaths.findIndex(path => currentSrc.includes(path.split('/').pop() || ''))
                      if (currentIndex >= 0 && currentIndex < tierImagePaths.length - 1) {
                        e.currentTarget.src = tierImagePaths[currentIndex + 1]
                      }
                    }}
                  />
                </div>
              )
            })()}
            
            {/* AGC: Center - Potion name */}
            <div className="flex-1">
              <h3 className="text-sm font-bold text-white">{selectedPotion.displayName}</h3>
              <p className="text-[10px] text-slate-400">{selectedEffectData?.description}</p>
              {/* AGC: Show effect details */}
              <div className="text-[9px] text-slate-500 mt-1">
                {selectedPotion.effect.type === PotionEffectType.Shield && (
                  <>🛡️ +{selectedPotion.effect.defenseBonus}% defense</>
                )}
                {selectedPotion.effect.type === PotionEffectType.Pierce && (
                  <>📈 +{selectedPotion.effect.attackBonus}% attack</>
                )}
                {selectedPotion.effect.type === PotionEffectType.Haste && (
                  <>⚡ +{selectedPotion.effect.flatDamage} flat damage</>
                )}
                {selectedPotion.effect.type === PotionEffectType.Frost && (
                  <>❄️ Freeze {selectedPotion.effect.freezeTurns} turn{selectedPotion.effect.freezeTurns > 1 ? 's' : ''}</>
                )}
                {selectedPotion.effect.type === PotionEffectType.Heal && (
                  <>❤️ +{selectedPotion.effect.hpRestored} HP</>
                )}
                {selectedPotion.effect.type === PotionEffectType.ManaRegen && (
                  <>💧 +{selectedPotion.effect.regenPercent}% mana regen</>
                )}
                {selectedPotion.effect.type === PotionEffectType.DoubleStrike && (
                  <>⚔️ +{selectedPotion.effect.damageBonus}% damage on win</>
                )}
                {selectedPotion.effect.type === PotionEffectType.Poison && (
                  <>☠️ {selectedPotion.effect.damagePerTurn} dmg × {selectedPotion.effect.turns} turns</>
                )}
              </div>
            </div>
            
            {/* AGC: Right - Effect icon */}
            {(() => {
              // AGC: Map effect type to icon file
              const getEffectIcon = (effectType: PotionEffectType): string => {
                switch (effectType) {
                  case PotionEffectType.Shield:
                    return '/shield.png'
                  case PotionEffectType.Pierce:
                    return '/pierce.png'
                  case PotionEffectType.Haste:
                    return '/haste.png'
                  case PotionEffectType.Frost:
                    return '/frost.png'
                  case PotionEffectType.Heal:
                    return '/potion_red.png' // AGC: Using potion icon for heal
                  case PotionEffectType.ManaRegen:
                    return '/potion_blue.png' // AGC: Using potion icon for mana
                  case PotionEffectType.DoubleStrike:
                    return '/pierce.png' // AGC: Using pierce icon for double strike
                  case PotionEffectType.Poison:
                    return '/potion_green.png' // AGC: Using potion icon for poison
                  default:
                    return '/haste.png'
                }
              }
              
              const effectIconPath = getEffectIcon(selectedPotion.effect.type)
              
              return (
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                  <img 
                    src={effectIconPath} 
                    alt={selectedEffectData?.name || 'Effect icon'}
                    className="w-full h-full object-contain drop-shadow-lg"
                  />
                </div>
              )
            })()}
          </div>

          {/* AGC: Recipe Costs - Compact list */}
          <div className="space-y-1.5 mb-3">
            <div className="text-[9px] uppercase tracking-wide text-slate-500 mb-1.5">Crafting Cost</div>
            
            {/* AGC: OGB Cost - Use on-chain price if available */}
            <div className="flex justify-between items-center text-[11px]">
              <span className="text-slate-300">OGB</span>
              <span className={`font-bold ${hasEnoughOGB ? 'text-white' : 'text-red-400'}`}>
                {onChainCraftingPrice ? (
                  <>
                    {onChainCraftingPrice.ogbPrice}
                    {onChainCraftingPrice.hasDiscount && (
                      <span className="text-green-400 text-[9px] ml-1">(50% off)</span>
                    )}
                  </>
                ) : (
                  formatEther(BigInt(preview.recipe.ogbCost))
                )}
              </span>
            </div>

            {/* AGC: Material Costs */}
            {Object.entries(preview.recipe.materialCost).map(([material, required]) => {
              const available = materials[material as keyof Materials] || 0
              const hasEnough = available >= required
              const emoji = material === 'herb' ? '🌿' : material === 'crystal' ? '💎' : material === 'essence' ? '✨' : '🔮'
              return (
                <div key={material} className="flex justify-between items-center text-[11px]">
                  <span className="text-slate-300 capitalize">
                    {emoji} {material}
                  </span>
                  <span className={`font-bold ${hasEnough ? 'text-white' : 'text-red-400'}`}>
                    {available} / {required}
                  </span>
                </div>
              )
            })}
          </div>

          {/* AGC: Stats Grid - Compact 3-column */}
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            <div className="bg-slate-900/50 rounded-lg p-2 text-center border border-white/5">
              <div className="text-[9px] text-slate-400 mb-0.5">Success</div>
              <div className="text-lg font-extrabold text-cyan-300">
                {(preview.successRate * 100).toFixed(0)}%
              </div>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-2 text-center border border-white/5">
              <div className="text-[9px] text-slate-400 mb-0.5">Magnitude</div>
              <div className="text-lg font-extrabold text-purple-300">
                +{(preview.magnitude * 100).toFixed(0)}%
              </div>
            </div>
            <div className="bg-slate-900/50 rounded-lg p-2 text-center border border-white/5">
              <div className="text-[9px] text-slate-400 mb-0.5">FarmPro</div>
              <div className="text-lg font-extrabold text-emerald-300">
                {(preview.farmPro * 100).toFixed(0)}%
              </div>
            </div>
          </div>

          {/* AGC: Craft Button - Primary action always visible */}
          <button
            onClick={handleCraft}
            disabled={!hasRequiredMaterials || !hasEnoughOGB || isCrafting || isLoadingPreview}
            className={`w-full py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
              hasRequiredMaterials && hasEnoughOGB && !isCrafting
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg shadow-green-500/30'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed'
            }`}
          >
            <FlaskConical className="w-4 h-4" />
            {isCrafting ? 'Crafting...' : 'Craft Potion'}
          </button>

          {/* AGC: Error Messages */}
          {!hasRequiredMaterials && (
            <p className="text-[10px] text-red-400 mt-2 text-center">⚠️ Insufficient materials</p>
          )}
          {!hasEnoughOGB && (
            <p className="text-[10px] text-red-400 mt-2 text-center">⚠️ Insufficient OGB balance</p>
          )}
        </section>
      )}

      {/* AGC: Craft Result */}
      {craftResult && (
        <div className={`rounded-xl p-3 border ${
          craftResult.success
            ? 'bg-green-900/40 border-green-500 text-green-200'
            : 'bg-red-900/40 border-red-500 text-red-200'
        }`}>
          <p className="text-[11px] font-semibold text-center">{craftResult.message}</p>
        </div>
      )}

      {/* AGC: Loading State */}
      {isLoadingPreview && (
        <div className="bg-slate-950/60 border border-slate-900 rounded-2xl p-6 text-center">
          <p className="text-slate-400 text-sm">Loading crafting preview...</p>
        </div>
      )}

      {/* AGC: Rarity Result Popup Modal - Centered, auto-closes after 5 seconds */}
      {rarityResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-slate-950 to-slate-900 border-2 border-white/20 rounded-2xl p-6 mx-4 max-w-sm shadow-[0_12px_32px_rgba(0,0,0,0.8)] relative">
            {/* AGC: Close button */}
            <button
              onClick={() => setRarityResult(null)}
              className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* AGC: Rarity display with color-coded text */}
            <div className="text-center mb-4">
              <div className="text-6xl mb-3">
                {rarityResult.rarity === 0 && '⚪'} {/* Common */}
                {rarityResult.rarity === 1 && '🟢'} {/* Uncommon */}
                {rarityResult.rarity === 2 && '🔵'} {/* Rare */}
                {rarityResult.rarity === 3 && '🟣'} {/* Epic */}
                {rarityResult.rarity === 4 && '🟠'} {/* Legendary - Orange */}
              </div>
              <h2 className={`text-2xl font-bold mb-1 ${
                rarityResult.rarity === 0 ? 'text-slate-300' : // Common
                rarityResult.rarity === 1 ? 'text-green-400' : // Uncommon
                rarityResult.rarity === 2 ? 'text-blue-400' : // Rare
                rarityResult.rarity === 3 ? 'text-purple-400' : // Epic
                'text-orange-400' // Legendary
              }`}>
                {rarityResult.rarityName} {rarityResult.potionName}
              </h2>
              <p className="text-sm text-slate-400">Token #{rarityResult.tokenId}</p>
            </div>

            {/* AGC: Magnitude display - Format: magnitude / 100 = percentage */}
            <div className="bg-slate-900/50 rounded-lg p-3 mb-4 text-center border border-white/10">
              <p className="text-xs text-slate-400 mb-1">Effect Magnitude</p>
              <p className="text-xl font-bold text-purple-300">
                {(() => {
                  // AGC: Magnitude is stored as integer (e.g., 2500 = 25.0%)
                  // AGC: Divide by 100 to get percentage, then format
                  const percentage = rarityResult.magnitude / 100
                  return percentage % 1 === 0 
                    ? `+${percentage.toFixed(0)}%` 
                    : `+${percentage.toFixed(1)}%`
                })()}
              </p>
            </div>

            {/* AGC: Auto-close indicator */}
            <div className="text-center">
              <p className="text-xs text-slate-500">This popup will close automatically in 5 seconds</p>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}
